import {
  DEFAULT_FIXED_STAKE,
  MAX_PROFIT_MATRIX_AXIS,
  MAX_PROFIT_MATRIX_CELLS,
} from '../shared/constants.ts';

export { DEFAULT_FIXED_STAKE, MAX_PROFIT_MATRIX_AXIS, MAX_PROFIT_MATRIX_CELLS };

export interface EntryStopRange {
  entryFrom: number;
  entryTo: number;
  stopFrom: number;
  stopTo: number;
}

export interface FixedGridCell {
  entryDrought: number;
  stopDrought: number;
  wagerCount: number;
}

export class MatrixRangeError extends Error {
  readonly requestedCells: number;
  readonly limit: number;

  constructor(requestedCells: number, limit = MAX_PROFIT_MATRIX_CELLS) {
    super(matrixRangeTooLargeMessage(requestedCells, limit));
    this.name = 'MatrixRangeError';
    this.requestedCells = requestedCells;
    this.limit = limit;
  }
}

export function fixedWagerCount(entryDrought: number, stopDrought: number): number {
  return stopDrought - entryDrought;
}

export function isValidFixedMatrixCell(entryDrought: number, stopDrought: number): boolean {
  return (
    Number.isInteger(entryDrought) &&
    entryDrought >= 1 &&
    Number.isInteger(stopDrought) &&
    stopDrought >= entryDrought + 1
  );
}

export function theoreticalFixedFullStopExposure(wagerCount: number, baseStake = DEFAULT_FIXED_STAKE): number {
  if (!Number.isInteger(wagerCount) || wagerCount < 1) {
    throw new Error('Wager count W must be an integer >= 1.');
  }
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  return baseStake * wagerCount;
}

export function fixedStakeProgression(wagerCount: number, baseStake = DEFAULT_FIXED_STAKE): number[] {
  if (!Number.isInteger(wagerCount) || wagerCount < 1) {
    throw new Error('Wager count W must be an integer >= 1.');
  }
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('baseStake must be a finite number > 0.');
  }
  return Array.from({ length: wagerCount }, () => baseStake);
}

/** Winning wager nets stake*(M-1); each prior loss in the episode is -stake. */
export function fixedHitNetPnl(targetMultiplier: number, priorLosingWagers: number, stake = DEFAULT_FIXED_STAKE): number {
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
    throw new Error('Target multiplier must be a finite number >= 1.');
  }
  if (!Number.isInteger(priorLosingWagers) || priorLosingWagers < 0) {
    throw new Error('Prior losing wagers must be an integer >= 0.');
  }
  if (!Number.isFinite(stake) || stake <= 0) {
    throw new Error('Stake must be a finite number > 0.');
  }
  return stake * (targetMultiplier - 1) - priorLosingWagers * stake;
}

export function fixedStopNetPnl(wagerCount: number, stake = DEFAULT_FIXED_STAKE): number {
  return -theoreticalFixedFullStopExposure(wagerCount, stake);
}

export function requestedMatrixCellCount(range: EntryStopRange): number {
  const { entryFrom, entryTo, stopFrom, stopTo } = range;
  return (entryTo - entryFrom + 1) * (stopTo - stopFrom + 1);
}

export function matrixRangeTooLargeMessage(requestedCells: number, limit = MAX_PROFIT_MATRIX_CELLS): string {
  return `Requested matrix has ${requestedCells.toLocaleString('en-US')} cells (limit ${limit.toLocaleString('en-US')}). Narrow the Entry D and Stop S ranges. The requested range was not truncated.`;
}

export function assertMatrixRange(range: EntryStopRange, limit = MAX_PROFIT_MATRIX_CELLS): void {
  const { entryFrom, entryTo, stopFrom, stopTo } = range;
  if (
    !Number.isInteger(entryFrom) ||
    !Number.isInteger(entryTo) ||
    !Number.isInteger(stopFrom) ||
    !Number.isInteger(stopTo)
  ) {
    throw new Error('Entry D and Stop S bounds must be integers.');
  }
  if (entryFrom < 1 || stopFrom < 1) {
    throw new Error('Entry D and Stop S must be >= 1.');
  }
  if (entryTo < entryFrom) {
    throw new Error('Entry drought range must have from ≤ to.');
  }
  if (stopTo < stopFrom) {
    throw new Error('Stop drought range must have from ≤ to.');
  }
  if (
    entryFrom > MAX_PROFIT_MATRIX_AXIS ||
    entryTo > MAX_PROFIT_MATRIX_AXIS ||
    stopFrom > MAX_PROFIT_MATRIX_AXIS ||
    stopTo > MAX_PROFIT_MATRIX_AXIS
  ) {
    throw new Error(`Entry D and Stop S must be ≤ ${MAX_PROFIT_MATRIX_AXIS.toLocaleString('en-US')}.`);
  }
  const count = requestedMatrixCellCount(range);
  if (count > limit) {
    throw new MatrixRangeError(count, limit);
  }
}

/**
 * Target-aware D/S defaults. Higher M needs deeper droughts; 20x includes D40/S60.
 * These are starting points, not strategy recommendations.
 */
export function defaultEntryStopRange(targetMultiplier: number): EntryStopRange {
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
    throw new Error('Target multiplier must be a finite number >= 1.');
  }
  if (targetMultiplier <= 2.5) {
    return { entryFrom: 2, entryTo: 10, stopFrom: 4, stopTo: 21 };
  }
  if (targetMultiplier <= 4) {
    return { entryFrom: 3, entryTo: 15, stopFrom: 6, stopTo: 32 };
  }
  if (targetMultiplier <= 7) {
    return { entryFrom: 5, entryTo: 25, stopFrom: 10, stopTo: 50 };
  }
  if (targetMultiplier <= 12) {
    return { entryFrom: 10, entryTo: 40, stopFrom: 20, stopTo: 70 };
  }
  if (targetMultiplier <= 25) {
    return { entryFrom: 20, entryTo: 50, stopFrom: 40, stopTo: 80 };
  }
  return { entryFrom: 30, entryTo: 80, stopFrom: 50, stopTo: 130 };
}

export function enumerateFixedMatrixCells(range: EntryStopRange): FixedGridCell[] {
  assertMatrixRange(range);
  const cells: FixedGridCell[] = [];
  for (let entryDrought = range.entryFrom; entryDrought <= range.entryTo; entryDrought += 1) {
    for (let stopDrought = range.stopFrom; stopDrought <= range.stopTo; stopDrought += 1) {
      if (!isValidFixedMatrixCell(entryDrought, stopDrought)) {
        continue;
      }
      cells.push({
        entryDrought,
        stopDrought,
        wagerCount: fixedWagerCount(entryDrought, stopDrought),
      });
    }
  }
  return cells;
}

export function axisValues(from: number, to: number): number[] {
  const values: number[] = [];
  for (let value = from; value <= to; value += 1) {
    values.push(value);
  }
  return values;
}

/** 0..1 heat weight from |P&L| relative to the matching signed extreme in the grid. */
export function pnlHeatWeight(pnl: number, maxPositive: number, maxNegative: number): number {
  if (!Number.isFinite(pnl) || pnl === 0) {
    return 0;
  }
  const scale = pnl > 0 ? maxPositive : maxNegative;
  if (!(scale > 0)) {
    return 0;
  }
  return Math.min(1, Math.abs(pnl) / scale);
}

/**
 * Display-only heat weight. Separate positive/negative scales plus a square-root
 * so one extreme outlier saturates instead of flattening every other cell.
 * Does not change stored P&L or profit/exposure values.
 */
export function signedHeatWeight(value: number, maxPositive: number, maxNegative: number): number {
  return Math.sqrt(pnlHeatWeight(value, maxPositive, maxNegative));
}
